
import React from "react";
import { Button } from "@/components/ui/button";
import { 
  Edit, 
  Trash2, 
  Power,
  UserPlus,
  UserMinus
} from "lucide-react";
import { ProviderWithUser } from "@/types/strategy-types";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface ProviderActionsProps {
  provider: ProviderWithUser;
  onEdit: (provider: ProviderWithUser) => void;
  onDelete: (providerId: string) => void;
  onToggleActive: (providerId: string, isActive: boolean) => void;
  onAssignUser: (providerId: string) => void;
  onRemoveUser: (providerId: string, userId: string) => void;
}

const ProviderActions = ({
  provider,
  onEdit,
  onDelete,
  onToggleActive,
  onAssignUser,
  onRemoveUser
}: ProviderActionsProps) => {
  return (
    <div className="flex items-center justify-end space-x-1">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onToggleActive(provider.id, provider.active || false)}
              className="h-8 w-8"
            >
              <Power className={`h-4 w-4 ${provider.active ? 'text-green-500' : 'text-red-500'}`} />
              <span className="sr-only">{provider.active ? 'Deactivate' : 'Activate'}</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{provider.active ? 'Deactivate' : 'Activate'} provider</p>
          </TooltipContent>
        </Tooltip>
        
        {provider.user ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onRemoveUser(provider.id, provider.user?.id || '')}
                className="h-8 w-8"
              >
                <UserMinus className="h-4 w-4 text-orange-500" />
                <span className="sr-only">Remove user</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Remove assigned user</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onAssignUser(provider.id)}
                className="h-8 w-8"
              >
                <UserPlus className="h-4 w-4 text-blue-500" />
                <span className="sr-only">Assign user</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Assign user</p>
            </TooltipContent>
          </Tooltip>
        )}
        
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(provider)}
              className="h-8 w-8"
            >
              <Edit className="h-4 w-4" />
              <span className="sr-only">Edit</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Edit provider</p>
          </TooltipContent>
        </Tooltip>
        
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(provider.id)}
              className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
              <span className="sr-only">Delete</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Delete provider</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

export default ProviderActions;
